import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MANAGE_AI_SUB_TEXT } from "@/utils/constants/messages";
import { useFeaturePermission } from "@/utils/hooks/useFeaturePermission";
import { ManageAiSkeleton } from "@/components/skeletons";
import UploadAiCredentialModal from "@/components/modals/UploadAiCredentialModal";
import CredentialTypeSelectionModal from "@/components/modals/CredentialTypeSelectionModal";
import { HEADINGS, BUTTONS } from "@/utils/constants/appConstants/commonConstants";
import { getAiConfiguration, getLLMProviderList } from "@/services/setting.service";
import { AI_PROVIDER } from "@/utils/constants/constants";
import { getMaskedApiKey } from "@/utils/functions/helperFunctions";
import { FEATURE_PERMISSIONS } from "@/utils/constants/featurePermissions";

type FieldValue = string | number | boolean | null;

interface AiConfiguration {
    _id: string;
    provider: string;
    models: Array<{ label: string; value: string }>;
    encrypted_api_key: string;
    is_default: boolean;
    meta_data: {
        tokens_used: number;
        deployment_name: string;
        endpoint: string;
        api_version: string;
    };
    created_at: string;
    deleted: string;
}

type ProviderRowProps = {
    providerKey: string;
    title: string;
    canAdd?: boolean;
    aiConfigurations: AiConfiguration[];
    showApiKey: Record<string, boolean>;
    isViewLLMCredentialPermission: boolean;
    onUpload: (provider: string) => void;
    onToggleApiKey: (provider: string) => void;
    onManage: (id: string) => void;
};

function ProviderRow({
    providerKey,
    title,
    canAdd = true,
    aiConfigurations,
    showApiKey,
    isViewLLMCredentialPermission,
    onUpload,
    onToggleApiKey,
    onManage,
}: ProviderRowProps) {
    const credentials = aiConfigurations.filter((config) => config.provider === providerKey);
    const credentialsCount = credentials.length;
    const hasCredentials = credentialsCount > 0;
    const isApiKeyVisible = Boolean(showApiKey[providerKey]);

    return (
        <div className="gs-manage-ai-row">
            <div className="gs-manage-ai-row-main">
                <div className="gs-manage-ai-row-left">
                    <p className="gs-manage-ai-provider-name">{title}</p>
                    {hasCredentials && (
                        <div className="gs-manage-ai-meta">
                            <span className="gs-manage-ai-meta-text">
                                {credentialsCount} {HEADINGS.CREDENTIALS}
                            </span>
                            <button
                                type="button"
                                className="gs-manage-ai-meta-action"
                                onClick={() => onToggleApiKey(providerKey)}
                            >
                                {isApiKeyVisible ? BUTTONS.HIDE_API_KEY : BUTTONS.SHOW_API_KEY}
                            </button>
                        </div>
                    )}
                </div>
                {(!hasCredentials || canAdd) && (
                    <button
                        type="button"
                        className="gs-manage-ai-action"
                        onClick={() => onUpload(providerKey)}
                    >
                        {HEADINGS.UPLOAD_CREDENTIALS}
                    </button>
                )}
            </div>
            {hasCredentials && (
                <div className={`gs-manage-ai-api-panel${isApiKeyVisible ? " is-visible" : ""}`}>
                    {credentials.map((config) => (
                        <div key={config._id} className="gs-manage-ai-api-line">
                            <span>
                                <strong>{HEADINGS.API_KEY}:</strong> {getMaskedApiKey(config.encrypted_api_key)}
                            </span>
                            {isViewLLMCredentialPermission && (
                                <button
                                    type="button"
                                    className="gs-manage-ai-action"
                                    onClick={() => onManage(config._id)}
                                >
                                    {HEADINGS.MANAGE}
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function ManageAi() {
    const navigate = useNavigate();
    const [aiConfigurations, setAiConfigurations] = useState<AiConfiguration[]>([]);
    const [llmProviderList, setLlmProviderList] = useState<Record<string, unknown[]>>({});
    const [loading, setLoading] = useState(false);
    const [refresh, setRefresh] = useState(false);
    const [showApiKey, setShowApiKey] = useState<Record<string, boolean>>({});
    const [showCredentialTypeSelectionModal, setShowCredentialTypeSelectionModal] = useState(false);
    const [selectedProvider, setSelectedProvider] = useState("");
    const [credentialType, setCredentialType] = useState<"text" | "embedding">("text");
    const [showOpenAiModal, setShowOpenAiModal] = useState(false);
    const [showAzureOpenAiModal, setShowAzureOpenAiModal] = useState(false);
    const [showClaudeAiModal, setShowClaudeAiModal] = useState(false);
    const [showGeminiModal, setShowGeminiModal] = useState(false);
    const [openAiApiKey, setOpenAiApiKey] = useState("");
    const [openAiAllowMessagesLimit, setOpenAiAllowMessagesLimit] = useState(false);
    const [openAiMessagesLimit, setOpenAiMessagesLimit] = useState<number | null>(null);
    const [azureApiKey, setAzureApiKey] = useState("");
    const [azureEndpoint, setAzureEndpoint] = useState("");
    const [azureDeployment, setAzureDeployment] = useState("");
    const [azureApiVersion, setAzureApiVersion] = useState("");
    const [azureAllowMessagesLimit, setAzureAllowMessagesLimit] = useState(false);
    const [azureMessagesLimit, setAzureMessagesLimit] = useState<number | null>(null);
    const [claudeApiKey, setClaudeApiKey] = useState("");
    const [claudeAllowMessagesLimit, setClaudeAllowMessagesLimit] = useState(false);
    const [claudeMessagesLimit, setClaudeMessagesLimit] = useState<number | null>(null);
    const [geminiApiKey, setGeminiApiKey] = useState("");
    const [geminiAllowMessagesLimit, setGeminiAllowMessagesLimit] = useState(false);
    const [geminiMessagesLimit, setGeminiMessagesLimit] = useState<number | null>(null);
    const isViewLLMCredentialPermission = useFeaturePermission(FEATURE_PERMISSIONS.AdminPortalUpdateLLMCredential);

    const refreshSettings = () => setRefresh((prev) => !prev);

    useEffect(() => {
        getLLMProviderList((response: Record<string, unknown[]>) => setLlmProviderList(response ?? {}));
        getAiConfiguration(
            (response: AiConfiguration[]) => setAiConfigurations(response),
            () => setLoading(false)
        );
    }, [refresh]);

    const getProviderModels = (provider: string, type: "text" | "embedding") =>
        (llmProviderList[provider] as Array<{ label: string; value: string; type: string }> | undefined)?.filter(
            (model) => model.type === type
        ) ?? [];

    const openUploadCredentialModal = (provider: string) => {
        setSelectedProvider(provider);
        setShowCredentialTypeSelectionModal(true);
    };

    const openProviderUploadModal = (provider: string) => {
        switch (provider) {
            case AI_PROVIDER.OPENAI:
                setShowOpenAiModal(true);
                break;
            case AI_PROVIDER.AZURE_OPENAI:
                setShowAzureOpenAiModal(true);
                break;
            case AI_PROVIDER.CLAUDE_AI:
                setShowClaudeAiModal(true);
                break;
            case AI_PROVIDER.GEMINI:
                setShowGeminiModal(true);
                break;
        }
    };

    const handleCredentialTypeSelected = (type: "text" | "embedding") => {
        setCredentialType(type);
        setShowCredentialTypeSelectionModal(false);
        openProviderUploadModal(selectedProvider);
    };

    const getProviderTitle = (provider: string) => {
        switch (provider) {
            case AI_PROVIDER.AZURE_OPENAI:
                return HEADINGS.AZURE_OPEN_AI;
            case AI_PROVIDER.OPENAI:
                return HEADINGS.OPEN_AI;
            case AI_PROVIDER.CLAUDE_AI:
                return HEADINGS.CLAUDE_AI;
            case AI_PROVIDER.GEMINI:
                return HEADINGS.GEMINI_AI;
            default:
                return "";
        }
    };

    const toggleApiKey = (provider: string) =>
        setShowApiKey((prev) => ({ ...prev, [provider]: !prev[provider] }));

    const providerRowProps = {
        aiConfigurations,
        showApiKey,
        isViewLLMCredentialPermission,
        onUpload: openUploadCredentialModal,
        onToggleApiKey: toggleApiKey,
        onManage: (id: string) => navigate(`/manage-ai/${id}`),
    };

    return (
        <div>
            <div className="gs-manage-ai-card">
                <div className="gs-manage-ai-shell">
                    <div className="gs-manage-ai-header">
                        <h4 className="gs-manage-ai-title" id="setting-license-title">
                            {HEADINGS.AI_CONFIGURATIONS}
                        </h4>
                        <p className="gs-manage-ai-subtitle" id="setting-license-sub-heading">
                            {MANAGE_AI_SUB_TEXT}
                        </p>
                    </div>

                    <div className="gs-manage-ai-list">
                        {llmProviderList && !loading ? (
                            <>
                                <ProviderRow providerKey={AI_PROVIDER.OPENAI} title={HEADINGS.OPEN_AI} canAdd={false} {...providerRowProps} />
                                <ProviderRow providerKey={AI_PROVIDER.AZURE_OPENAI} title={HEADINGS.AZURE_OPEN_AI} canAdd {...providerRowProps} />
                                <ProviderRow providerKey={AI_PROVIDER.CLAUDE_AI} title={HEADINGS.CLAUDE_AI} canAdd={false} {...providerRowProps} />
                                <ProviderRow providerKey={AI_PROVIDER.GEMINI} title={HEADINGS.GEMINI_AI} canAdd={false} {...providerRowProps} />
                            </>
                        ) : (
                            <ManageAiSkeleton />
                        )}
                    </div>
                </div>
            </div>

            <CredentialTypeSelectionModal
                show={showCredentialTypeSelectionModal}
                setShowModal={setShowCredentialTypeSelectionModal}
                onSelectType={handleCredentialTypeSelected}
                title={getProviderTitle(selectedProvider)}
            />

            <UploadAiCredentialModal
                show={showOpenAiModal}
                setShowModal={setShowOpenAiModal}
                refreshSettings={refreshSettings}
                title={HEADINGS.OPEN_AI}
                provider={AI_PROVIDER.OPENAI}
                fields={[
                    { id: "api-key", label: HEADINGS.API_KEY, type: "text", value: openAiApiKey, setValue: (value: FieldValue) => setOpenAiApiKey(value as string) },
                    { id: "allow-messages-limit", label: HEADINGS.ALLOW_MESSAGES_LIMIT, type: "checkbox", value: openAiAllowMessagesLimit, className: "ml-2 size-lg", setValue: (value: FieldValue) => setOpenAiAllowMessagesLimit(value as boolean) },
                    { id: "messages-limit", label: HEADINGS.MESSAGES_LIMIT, type: "number", disabled: !openAiAllowMessagesLimit, value: openAiMessagesLimit, setValue: (value: FieldValue) => setOpenAiMessagesLimit(parseInt(value as string)) },
                ]}
                textModelOptions={getProviderModels(AI_PROVIDER.OPENAI, "text")}
                embeddingModelOptions={getProviderModels(AI_PROVIDER.OPENAI, "embedding")}
                isMultiSelect
                credentialType={credentialType}
            />

            <UploadAiCredentialModal
                show={showAzureOpenAiModal}
                setShowModal={setShowAzureOpenAiModal}
                refreshSettings={refreshSettings}
                title={HEADINGS.AZURE_OPEN_AI}
                provider={AI_PROVIDER.AZURE_OPENAI}
                fields={[
                    { id: "api-key", label: HEADINGS.API_KEY, type: "text", value: azureApiKey, setValue: (value: FieldValue) => setAzureApiKey(value as string) },
                    { id: "endpoint", label: HEADINGS.ENDPOINT, type: "text", value: azureEndpoint, setValue: (value: FieldValue) => setAzureEndpoint(value as string) },
                    { id: "deployment-name", label: HEADINGS.DEPLOYMENT, type: "text", value: azureDeployment, setValue: (value: FieldValue) => setAzureDeployment(value as string) },
                    { id: "api-version", label: HEADINGS.API_VERSION, type: "text", value: azureApiVersion, setValue: (value: FieldValue) => setAzureApiVersion(value as string) },
                    { id: "allow-messages-limit", label: HEADINGS.ALLOW_MESSAGES_LIMIT, type: "checkbox", value: azureAllowMessagesLimit, className: "ml-2 size-lg", setValue: (value: FieldValue) => setAzureAllowMessagesLimit(value as boolean) },
                    { id: "messages-limit", label: HEADINGS.MESSAGES_LIMIT, type: "number", disabled: !azureAllowMessagesLimit, value: azureMessagesLimit, setValue: (value: FieldValue) => setAzureMessagesLimit(parseInt(value as string)) },
                ]}
                textModelOptions={getProviderModels(AI_PROVIDER.AZURE_OPENAI, "text")}
                embeddingModelOptions={getProviderModels(AI_PROVIDER.AZURE_OPENAI, "embedding")}
                isMultiSelect={false}
                credentialType={credentialType}
            />

            <UploadAiCredentialModal
                show={showClaudeAiModal}
                setShowModal={setShowClaudeAiModal}
                refreshSettings={refreshSettings}
                title={HEADINGS.CLAUDE_AI}
                provider={AI_PROVIDER.CLAUDE_AI}
                fields={[
                    { id: "api-key", label: HEADINGS.API_KEY, type: "text", value: claudeApiKey, setValue: (value: FieldValue) => setClaudeApiKey(value as string) },
                    { id: "allow-messages-limit", label: HEADINGS.ALLOW_MESSAGES_LIMIT, type: "checkbox", value: claudeAllowMessagesLimit, className: "ml-2 size-lg", setValue: (value: FieldValue) => setClaudeAllowMessagesLimit(value as boolean) },
                    { id: "messages-limit", label: HEADINGS.MESSAGES_LIMIT, type: "number", disabled: !claudeAllowMessagesLimit, value: claudeMessagesLimit, setValue: (value: FieldValue) => setClaudeMessagesLimit(parseInt(value as string)) },
                ]}
                textModelOptions={getProviderModels(AI_PROVIDER.CLAUDE_AI, "text")}
                embeddingModelOptions={getProviderModels(AI_PROVIDER.CLAUDE_AI, "embedding")}
                isMultiSelect
                credentialType={credentialType}
            />

            <UploadAiCredentialModal
                show={showGeminiModal}
                setShowModal={setShowGeminiModal}
                refreshSettings={refreshSettings}
                title={HEADINGS.GEMINI_AI}
                provider={AI_PROVIDER.GEMINI}
                fields={[
                    { id: "api-key", label: HEADINGS.API_KEY, type: "text", value: geminiApiKey, setValue: (value: FieldValue) => setGeminiApiKey(value as string) },
                    { id: "allow-messages-limit", label: HEADINGS.ALLOW_MESSAGES_LIMIT, type: "checkbox", value: geminiAllowMessagesLimit, className: "ml-2 size-lg", setValue: (value: FieldValue) => setGeminiAllowMessagesLimit(value as boolean) },
                    { id: "messages-limit", label: HEADINGS.MESSAGES_LIMIT, type: "number", disabled: !geminiAllowMessagesLimit, value: geminiMessagesLimit, setValue: (value: FieldValue) => setGeminiMessagesLimit(parseInt(value as string)) },
                ]}
                textModelOptions={getProviderModels(AI_PROVIDER.GEMINI, "text")}
                embeddingModelOptions={getProviderModels(AI_PROVIDER.GEMINI, "embedding")}
                isMultiSelect
                credentialType={credentialType}
            />
        </div>
    );
}

export default ManageAi;
